import { useState } from 'react';
import { FileText, Film, Music, Play, Download, ChevronDown } from 'lucide-react';

// Shared utility: collect all files from a project with source metadata
export function collectProjectFiles(project) {
  const files = [];
  for (const m of project.messages) {
    if (m.files) {
      const source = m.type === 'user' ? 'client' : m.type === 'team' ? 'team' : null;
      for (const f of m.files) {
        files.push({ ...f, project: project.name, source: source || 'client' });
      }
    }
    if (m.type === 'delivery' && m.delivery?.files) {
      for (const f of m.delivery.files) {
        files.push({ ...f, project: project.name, source: 'team' });
      }
    }
  }
  if (project.delivery?.files) {
    for (const f of project.delivery.files) {
      files.push({ ...f, project: project.name, source: 'team' });
    }
  }
  const seen = new Set();
  return files.filter(f => {
    const key = `${f.name}-${f.project}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function getExtLabel(name) {
  const ext = name.split('.').pop()?.toUpperCase();
  return ext || 'FILE';
}

function ProjectLabel({ name }) {
  if (!name) return null;
  return (
    <div className="px-3 pt-2.5 pb-0">
      <span className="text-[10px] text-gray-500 truncate block leading-tight">{name}</span>
    </div>
  );
}

function SourceBadge({ source }) {
  if (!source) return null;
  return (
    <span className={`absolute top-2.5 right-2.5 text-[9px] font-medium px-2 py-0.5 rounded-full ${
      source === 'team'
        ? 'bg-emerald-500/15 text-emerald-600'
        : 'bg-gray-900/10 text-gray-500'
    }`}>
      {source === 'team' ? 'Team' : 'You'}
    </span>
  );
}

function MediaItem({ file, index }) {
  const isImage = file.type === 'image';
  const isVideo = file.type === 'video';
  const isAudio = file.type === 'audio';

  if (isImage) {
    return (
      <div data-testid={`media-item-${index}`} className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200/50 group cursor-pointer border border-gray-200/30 hover:shadow-md transition-shadow">
        <div className="aspect-[4/3] flex items-center justify-center relative">
          <span className="text-xs text-gray-400 font-medium truncate px-4">{file.name}</span>
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors" />
          <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <Download className="w-4 h-4 text-gray-500" />
          </div>
        </div>
        <SourceBadge source={file.source} />
        <div className="px-3 py-2.5 bg-white/40">
          {file.project && <span className="text-[10px] text-gray-400 block truncate mb-0.5">{file.project}</span>}
          <span className="text-[11px] text-gray-700 font-medium block truncate">{file.name}</span>
          <span className="text-[10px] text-gray-400">{file.size}</span>
        </div>
      </div>
    );
  }

  if (isVideo) {
    return (
      <div data-testid={`media-item-${index}`} className="relative rounded-2xl overflow-hidden bg-gray-900/[0.03] group cursor-pointer border border-gray-200/30 hover:shadow-md transition-shadow">
        <div className="aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-200/60 flex items-center justify-center relative">
          <div className="w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform">
            <Play className="w-5 h-5 text-gray-700 ml-0.5" />
          </div>
        </div>
        <SourceBadge source={file.source} />
        <div className="px-3 py-2.5 bg-white/40">
          {file.project && <span className="text-[10px] text-gray-400 block truncate mb-0.5">{file.project}</span>}
          <span className="text-[11px] text-gray-700 font-medium block truncate">{file.name}</span>
          <span className="text-[10px] text-gray-400">{file.size}</span>
        </div>
      </div>
    );
  }

  if (isAudio) {
    const ext = getExtLabel(file.name);
    return (
      <div data-testid={`media-item-${index}`} className="relative rounded-2xl overflow-hidden bg-gray-900 group cursor-pointer border border-gray-800/20 hover:shadow-md transition-shadow">
        <div className="aspect-[4/3] flex flex-col items-center justify-center relative px-4">
          <Music className="w-6 h-6 text-white/30 mb-2" />
          <span className="text-sm font-bold text-white/90 tracking-wide">{ext}</span>
        </div>
        <SourceBadge source={file.source} />
        <div className="px-3 py-2.5 bg-gray-800">
          {file.project && <span className="text-[10px] text-gray-500 block truncate mb-0.5">{file.project}</span>}
          <span className="text-[11px] text-gray-300 font-medium block truncate">{file.name}</span>
          <span className="text-[10px] text-gray-500">{file.size}</span>
        </div>
      </div>
    );
  }

  // Document / archive
  const ext = getExtLabel(file.name);
  return (
    <div data-testid={`media-item-${index}`} className="relative rounded-2xl overflow-hidden bg-white/40 group cursor-pointer border border-gray-200/40 hover:shadow-md transition-shadow">
      <div className="aspect-[4/3] flex flex-col items-center justify-center relative px-4">
        <FileText className="w-6 h-6 text-gray-300 mb-2" />
        <span className="text-xs font-semibold text-gray-500 tracking-wide">{ext}</span>
      </div>
      <SourceBadge source={file.source} />
      <div className="px-3 py-2.5 bg-white/60">
        {file.project && <span className="text-[10px] text-gray-400 block truncate mb-0.5">{file.project}</span>}
        <span className="text-[11px] text-gray-700 font-medium block truncate">{file.name}</span>
        <span className="text-[10px] text-gray-400">{file.size}</span>
      </div>
    </div>
  );
}

export default function MediaArchive({ files, maxVisible = 12, showProjectLabel = true }) {
  const [expanded, setExpanded] = useState(false);

  const displayFiles = showProjectLabel ? files : files.map(({ project, ...f }) => f);
  const visible = expanded ? displayFiles : displayFiles.slice(0, maxVisible);
  const hasMore = displayFiles.length > maxVisible;

  if (displayFiles.length === 0) {
    return (
      <div className="text-center py-10 text-gray-400">
        <FileText className="w-8 h-8 mx-auto mb-2 opacity-20" />
        <p className="text-xs">No files yet.</p>
      </div>
    );
  }

  return (
    <div data-testid="media-archive">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {visible.map((f, i) => (
          <MediaItem key={`${f.name}-${i}`} file={f} index={i} />
        ))}
      </div>
      {hasMore && !expanded && (
        <button
          data-testid="media-show-more"
          onClick={() => setExpanded(true)}
          className="w-full flex items-center justify-center gap-1 py-3 text-xs text-gray-400 hover:text-gray-600 transition-colors mt-3"
        >
          <ChevronDown className="w-3 h-3" />
          Show all ({displayFiles.length})
        </button>
      )}
    </div>
  );
}
